/**
 * Returns an optimised cover URL via the backend resize endpoint.
 * Falls back to the original URL if it can't be converted.
 *
 * @param {string} coverUrl  - e.g. "/covers/rec123.jpg" or a full URL
 * @param {number} width     - desired pixel width (default 300 for cards)
 */
export const optimizedCover = (coverUrl, width = 300) => {
  if (!coverUrl) return null;
  // Extract just the filename from paths like "/covers/rec123.jpg"
  const filename = coverUrl.split('/').pop();
  if (!filename) return coverUrl;
  return `/api/covers/${filename}?w=${width}`;
};

/**
 * Build a responsive srcSet so retina / high-DPR screens load the right
 * resolution. Browser picks the smallest variant matching the CSS pixel
 * size × device pixel ratio.
 *
 * Usage on an <img> displayed at ~183px wide:
 *   <img
 *     src={optimizedCover(url, 300)}                  // 1x fallback
 *     srcSet={coverSrcSet(url, [150, 300, 450])}     // 1x/2x/3x retina
 *     sizes="(max-width: 640px) 50vw, (max-width: 1024px) 33vw, 200px"
 *   />
 *
 * @param {string} coverUrl
 * @param {number[]} widths   Desired pixel widths to offer (sorted ascending).
 * @returns {string|null}
 */
export const coverSrcSet = (coverUrl, widths = [150, 300, 450, 600]) => {
  if (!coverUrl) return null;
  const filename = coverUrl.split('/').pop();
  if (!filename) return null;
  return widths
    .map((w) => `/api/covers/${filename}?w=${w} ${w}w`)
    .join(', ');
};

/**
 * Airtable attachment URLs expire (often HTTP 410). Prefer local /api/covers.
 */
export const isTemporaryCoverUrl = (url) => {
  if (!url || typeof url !== 'string') return false;
  const lower = url.toLowerCase();
  return (
    lower.includes('airtableusercontent.com') ||
    lower.includes('dl.airtable.com') ||
    lower.includes('airtable.com/attachments')
  );
};

const extractOnKapakUrl = (onKapak) => {
  if (!onKapak) return '';
  if (typeof onKapak === 'string') return onKapak;
  if (Array.isArray(onKapak) && onKapak.length > 0) {
    const first = onKapak[0];
    if (typeof first === 'object' && first?.url) return first.url;
    if (typeof first === 'string') return first;
  }
  return '';
};

/**
 * Resolve a public-facing book cover URL.
 * Prefer stable local covers (/covers/{id}.jpg → /api/covers/...) over
 * temporary Airtable attachment URLs which expire.
 *
 * @param {object} bookData
 * @param {number} width
 * @returns {string|null}
 */
export const getBookCoverUrl = (bookData, width = 800) => {
  if (!bookData) return null;
  const fields = bookData.fields || bookData;
  const bookId = bookData.id || bookData.airtable_id;
  const normalizedId =
    typeof bookId === 'string' && bookId.startsWith('book_')
      ? bookId.slice(5)
      : bookId;

  if (fields.local_cover) {
    if (typeof fields.local_cover === 'string' && fields.local_cover.startsWith('/')) {
      return optimizedCover(fields.local_cover, width);
    }
    if (typeof fields.local_cover === 'string' && !isTemporaryCoverUrl(fields.local_cover)) {
      return fields.local_cover;
    }
  }

  const onKapakUrl = extractOnKapakUrl(fields['Ön Kapak']);
  if (onKapakUrl && onKapakUrl.startsWith('/')) {
    return optimizedCover(onKapakUrl, width);
  }

  // Local cover endpoint also serves BookFolder front_cover_* uploads.
  if (normalizedId) {
    return optimizedCover(`/covers/${normalizedId}.jpg`, width);
  }

  if (onKapakUrl && onKapakUrl.startsWith('http') && !isTemporaryCoverUrl(onKapakUrl)) {
    return onKapakUrl;
  }

  return null;
};

/**
 * Resolve back-cover URL for flip/gallery (local /covers/{id}_back.jpg preferred).
 */
export const getBookBackCoverUrl = (bookData, width = 800) => {
  if (!bookData) return null;
  const fields = bookData.fields || bookData;
  const bookId = bookData.id || bookData.airtable_id;
  const normalizedId =
    typeof bookId === 'string' && bookId.startsWith('book_')
      ? bookId.slice(5)
      : bookId;

  if (fields.local_back_cover && typeof fields.local_back_cover === 'string') {
    if (fields.local_back_cover.startsWith('/')) {
      return optimizedCover(fields.local_back_cover, width);
    }
    if (!isTemporaryCoverUrl(fields.local_back_cover)) {
      return fields.local_back_cover;
    }
  }

  const arka = fields['Arka Kapak'];
  let arkaUrl = '';
  if (typeof arka === 'string') arkaUrl = arka;
  else if (Array.isArray(arka) && arka[0]) {
    arkaUrl = typeof arka[0] === 'object' ? (arka[0].url || '') : String(arka[0]);
  }
  if (arkaUrl && arkaUrl.startsWith('/')) {
    return optimizedCover(arkaUrl, width);
  }

  if (normalizedId) {
    return optimizedCover(`/covers/${normalizedId}_back.jpg`, width);
  }
  return null;
};

/** Prefer low-KB _sm file for list cards when available (falls back to ?w=). */
export const listCoverUrl = (bookId, width = 300) => {
  if (!bookId) return null;
  const normalizedId =
    typeof bookId === 'string' && bookId.startsWith('book_')
      ? bookId.slice(5)
      : bookId;
  return optimizedCover(`/covers/${normalizedId}_sm.jpg`, width);
};

const COVER_FALLBACK_DATA_URL = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg"/>';

/**
 * <img onError> handler: retry absolute /api path, then local cover by book id.
 *
 * @param {Event} event
 * @param {string} [bookId]
 * @param {string} [backendUrl]
 */
export const handleCoverImageError = (event, bookId = '', backendUrl = '') => {
  const img = event.currentTarget;
  if (!img) return;
  const src = img.getAttribute('src') || '';

  if (!img.dataset.triedAbsoluteApi && src.startsWith('/api/')) {
    img.dataset.triedAbsoluteApi = '1';
    img.src = `${backendUrl}${src}`;
    return;
  }

  const normalizedId =
    typeof bookId === 'string' && bookId.startsWith('book_')
      ? bookId.slice(5)
      : bookId;

  if (normalizedId && !img.dataset.triedLocalCover) {
    img.dataset.triedLocalCover = '1';
    img.src = optimizedCover(`/covers/${normalizedId}.jpg`, 800);
    return;
  }

  img.src = COVER_FALLBACK_DATA_URL;
};
